<script setup lang="ts">
import { Loader2 } from 'lucide-vue-next'
import axios from 'axios'

import LoyaltyCreateStepFive from './components/LoyaltyCreateStepFive.vue'
import LoyaltyCreateStepFour from './components/LoyaltyCreateStepFour.vue'
import LoyaltyCreateStepOne from './components/LoyaltyCreateStepOne.vue'
import LoyaltyCreateStepThree from './components/LoyaltyCreateStepThree.vue'
import LoyaltyCreateStepTwo from './components/LoyaltyCreateStepTwo.vue'
import LoyaltyCreateStepZero from './components/LoyaltyCreateStepZero.vue'

import type { PartnerParameter } from '@/types/types'
const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const isLoaded = ref(true)
const isButtonLoading = ref(false)
const partnerParameters = ref<PartnerParameter[]>([])
const partners = ref([])
const { send } = useEvent()
const {
  activeStep,
  activeDirection,
  goToStep,
  email,
  password,
  goToExplanation,
  radioValue,
  selectedPartner,
  isAssociatedArray,
  partnerDevices,
  selectedStores,
  getPartnerDevices,
  updateSelectedStores,
  internalTokenPartners,
  tokenPartners,
  partnerWithCustomWebsiteUrl,
  posDeviceId
} = useLoyalty()
const { user, stores } = useLocalStorage()
const { create } = useNotification()
const { updateKeys } = useGlobalConfig()
const { isUrlValid } = useRegex()
const { openInNewTab } = useDOM()
const stepsNumber = ref(5)
const progress = computed(() => (activeStep.value / stepsNumber.value) * 100)

const stepTitles = {
  '1': t('pages.loyalty.create.chooseExperience'),
  '2': t('pages.loyalty.create.understandLoyalty'),
  '3': t('pages.loyalty.create.startLoyalty'),
  '4': t('pages.loyalty.create.connectIntegration'),
  '5': t('pages.loyalty.create.chooseIntegration')
}

const oauthFinalRedirectUri = computed(() =>
  selectedPartnerId.value?.id ? `/loyalty/create?step=5&partnerParameterId=${selectedPartnerId.value.id}` : null
)

const isButtonDisabled = computed(() => {
  if (activeStep.value === 3 && !radioValue.value) {
    return true
  }
  if (isPartnerAuthButtonDisabled.value) {
    return true
  }
  if (activeStep.value === 5 && partnerDevices.value.length === 0 && !posDeviceId.value) {
    return true
  }
  return false
})

const isPartnerAuthButtonDisabled = computed(() => {
  const hasInValidToken = isStep4Valid.value && selectedPartner.value?.apiToken?.length < 9
  const hasInValidApiUrl = activeStep.value === 4 && !isUrlValid(selectedPartner.value?.apiUrl)
  return (
    (activeStep.value === 4 && hasInValidToken) ||
    (partnerWithCustomWebsiteUrl.includes(selectedPartner.value?.partner?.slug) && hasInValidApiUrl)
  )
})

const getPartners = async () => {
  const { data } = await axios.get('partners', {
    params: {
      isVisibleInMarketplace: 1
    }
  })
  partners.value = data.data
}

const getPartnersParameters = async () => {
  const { data } = await axios.get('partner-parameters', {
    params: {
      merchantId: user.value.merchant.id,
      finalRedirectUri: oauthFinalRedirectUri.value
    }
  })
  partnerParameters.value = data.data
}

const getPartnerParametersFromRoute = () => {
  const partnerId = route.query?.partnerParameterId

  const foundPartnerParameters = partnerParameters.value.find(
    (partnerParameter) => partnerParameter.partner.id === Number(partnerId)
  )
  selectedPartner.value = foundPartnerParameters
  radioValue.value = {
    id: foundPartnerParameters.partner.slug,
    img: foundPartnerParameters.partner.logo
  }
}

const updatePartnerParameters = async (params?: Record<string, unknown>) => {
  if (params) {
    await axios.put(`/partner-parameters/${selectedPartner.value.id}`, {
      ...params
    })
    return
  }
  const data = partnerParameters.value.find((partnerParameter) => partnerParameter.partner.slug === radioValue.value.id)
  selectedPartner.value = data
}

const findSelectedPartnerData = async () => {
  const selectedPartnerData = partnerParameters.value.find(
    (partnerParameter) => partnerParameter.partner.slug === radioValue.value.id
  )
  if (!selectedPartnerData) {
    throw new Error('Partner does not exist')
  }
  selectedPartner.value = selectedPartnerData
}

const createPartnerParameters = async () => {
  try {
    await findSelectedPartnerData()
  } catch {
    const { data } = await axios.post(`/partner-parameters`, {
      isProductPriceSyncEnabled: false,
      isProductSyncEnabled: false,
      pointsByCurrencyUnit: 1,
      pointsClaimingDelay: 10,
      minimumAmount: 0,
      merchantId: user.value?.merchant?.id,
      partnerId: selectedPartnerId.value.id,
      storeIds: [...stores.value.map((s) => s.id)],
      isActive: true,
      finalRedirectUri: oauthFinalRedirectUri.value,
      apiUrl: selectedPartner.value?.apiUrl
    })

    selectedPartner.value = data.data
  }
}

const showError = (message: string) => {
  create('error', {
    text: t(message),
    timeout: 5000
  })
  isButtonLoading.value = false
}

const associateDevices = async () => {
  const requests = []
  if (isAssociatedArray.value.length > 0) {
    for (const id of isAssociatedArray.value) {
      requests.push(
        axios.post('/partner-integrations', {
          isActive: 1,
          partnerDeviceId: id.toString(),
          partnerParameterId: selectedPartner.value.id,
          storeId: selectedStores.value[id].id,
          isHardwareless: 1
        })
      )
    }
  } else {
    posDeviceId.value.forEach((id, index) => {
      requests.push(
        axios.post('/partner-integrations', {
          isActive: 1,
          partnerDeviceId: id.toString(),
          partnerParameterId: selectedPartner.value.id,
          storeId: index,
          isHardwareless: 1
        })
      )
    })
  }

  await Promise.all(requests)
}

const updateKeysAndRedirect = async () => {
  await updateKeys('merchants', {
    dematerializedLoyaltyStatus: 1
  })
  router.push({ name: 'LoyaltyDematerializedEdit' })
  isButtonLoading.value = false
}

const isStep4Valid = computed(() => {
  if (tokenPartners.includes(selectedPartner.value?.partner?.slug)) {
    return !!selectedPartner.value?.apiToken
  } else {
    return true
  }
})

const handlePartnerLogin = async () => {
  if (partnerParameters.value.length && partnerParameters.value[0].partner.slug === radioValue.value.id) {
    await updatePartnerParameters()
  } else {
    await createPartnerParameters()
  }
}

const handlePartnerAuth = async () => {
  try {
    await authenticate()
  } catch {
    isButtonLoading.value = false
    return
  }

  if (!isStep4Valid.value) {
    isButtonLoading.value = false
    return
  }
  await updatePartnerParameters({
    ...selectedPartner.value,
    storeIds: selectedPartner.value.stores.map((partnerStore) => partnerStore.id)
  })
  goToStep(true)

  isButtonLoading.value = false
}

const handleSubmit = async () => {
  isButtonLoading.value = true

  if (activeStep.value === 3) {
    await handlePartnerLogin()
    send('segment', 'loyalty:partners:clicked', {
      id: selectedPartner.value.id,
      name: selectedPartner.value.partner.name,
      slug: selectedPartner.value.partner.slug
    })
  }

  if (activeStep.value === 4) {
    handlePartnerAuth()
    return
  }

  if (![4, 5].includes(activeStep.value)) {
    goToStep(true)
    isButtonLoading.value = false
    return
  }

  if (activeStep.value === 5 && (isAssociatedArray.value.length > 0 || posDeviceId.value.length > 0)) {
    await associateDevices()
  } else if (activeStep.value === 5) {
    showError('pages.loyalty.create.fillAssociateError')
    return
  }

  await updateKeysAndRedirect()
  return
}

const selectedPartnerId = computed(() => partners.value.find((partner) => partner.slug === radioValue.value?.id))

const authenticateWithPartnerRedirection = async () => {
  const selectedPartnerSlug = selectedPartner.value.partner.slug
  let partnerAuthenticationUrl = selectedPartner.value?.authenticationUrl

  if (partnerWithCustomWebsiteUrl.includes(selectedPartnerSlug)) {
    await updatePartnerParameters({
      ...selectedPartner.value,
      storeIds: selectedPartner.value.stores.map((partnerStore) => partnerStore.id)
    })
    await getPartnersParameters()
    findSelectedPartnerData()
    partnerAuthenticationUrl = selectedPartner.value?.authenticationUrl
  }
  openInNewTab(partnerAuthenticationUrl)
  return
}

const authenticate = async () => {
  isButtonLoading.value = true
  const selectedPartnerSlug = selectedPartner.value.partner.slug
  const partnerAuthenticationUrl = selectedPartner.value?.authenticationUrl
  const partnerType = selectedPartner.value.partner.partnerAuthenticationType?.slug
  const canRedirectToPartnerAuth = !!partnerAuthenticationUrl && partnerType !== 'time-limited-token'

  if (canRedirectToPartnerAuth) {
    await authenticateWithPartnerRedirection()
    return false
  }

  try {
    // Will be delete when we will be able to retrieve authentication type
    if (internalTokenPartners.includes(selectedPartnerSlug)) {
      const axiosInstance = axios.create()
      await axiosInstance.post(
        '/partner/auth',
        {
          auth: {
            login: email.value,
            password: password.value
          },
          storeId: stores.value[0].id
        },
        {
          params: {
            partner: selectedPartnerSlug
          }
        }
      )
    }
  } catch (error) {
    create('error', {
      text: t('pages.loyalty.create.integrationError', { name: selectedPartner.value.name }),
      timeout: 5000
    })
    throw error
  }
  isButtonLoading.value = false
}

const nextButtonLabel = computed(() => {
  switch (activeStep.value) {
    case 4:
      if (tokenPartners.includes(selectedPartner.value.partner.slug) && isStep4Valid.value) {
        return t('pages.loyalty.create.createLoyalty')
      }
      return t('pages.loyalty.create.connect')
    case 5:
      return t('pages.loyalty.create.createLoyalty')
    default:
      return t('pages.loyalty.create.next')
  }
})

const activeComponent = computed(() => {
  switch (activeStep.value) {
    case 0:
      return LoyaltyCreateStepZero
    case 1:
      return LoyaltyCreateStepOne
    case 2:
      return LoyaltyCreateStepTwo
    case 3:
      return LoyaltyCreateStepThree
    case 4:
      return LoyaltyCreateStepFour
    default:
      return LoyaltyCreateStepFive
  }
})

const populatePageFromRoute = async () => {
  await getPartnersParameters()
  getPartnerParametersFromRoute()

  activeStep.value = Number(route.query.step)
  await getPartnerDevices()
  updateSelectedStores()
}

onMounted(async () => {
  isLoaded.value = false
  router.push({ query: { step: activeStep.value } })
  await getPartners()
  if (Number(route.query?.step) > 4) {
    await populatePageFromRoute()
  } else {
    await getPartnersParameters()
  }
  isLoaded.value = true
})
</script>

<template>
  <div v-if="isLoaded">
    <div
      class="flex min-h-screen items-center justify-center px-10 py-10 text-foreground xl:h-screen xl:px-36 xl:py-[4.5rem] 2xl:py-[8rem]"
    >
      <div class="relative flex h-full w-full max-w-[1000px] grow flex-col rounded-md border bg-white">
        <div v-if="activeStep !== 0">
          <div class="flex flex-col items-center justify-between px-10 py-6 md:flex-row">
            <h1 class="mb-2 text-center text-lg font-semibold text-foreground md:mb-0 md:text-left">
              {{ stepTitles[activeStep] }}
            </h1>
            <p class="hidden text-sm text-muted-foreground sm:block">
              {{ t('pages.loyalty.create.steps', { from: activeStep, to: stepsNumber }) }}
            </p>
          </div>
          <div class="overflow-hidden bg-primary/10">
            <div
              class="h-1 bg-primary transition-all duration-500 ease-in-out"
              :style="{ width: `${progress}%` }"
            ></div>
          </div>
        </div>
        <form class="flex h-full" @submit.prevent="handleSubmit()">
          <transition :name="activeDirection" mode="out-in">
            <component :is="activeComponent" />
          </transition>
          <div
            v-if="activeStep !== 1"
            class="absolute bottom-0 flex w-full flex-col items-center justify-between gap-3 rounded-b-md border-t bg-white px-10 py-3 xl:flex-row"
          >
            <Button
              type="button"
              variant="outline"
              class="min-w-[150px]"
              @click="() => (activeStep !== 0 ? goToStep(false) : goToExplanation(false))"
              >{{ t('pages.loyalty.create.previous') }}
            </Button>
            <Button
              v-if="activeStep !== 0"
              :disabled="isButtonDisabled || isButtonLoading"
              type="submit"
              variant="default-primary"
              class="min-w-[150px]"
            >
              <Loader2 v-if="isButtonLoading" class="animate-spin" />
              {{ nextButtonLabel }}
            </Button>
          </div>
        </form>
      </div>
    </div>
  </div>
  <TheLoadingPage v-else />
</template>

<style scoped>
.forward-enter-active,
.back-enter-active {
  transition: all ease-in-out 0.3s;
}

.forward-leave-active,
.back-leave-active {
  transition: all ease-in-out 0.3s;
}

.forward-enter {
  opacity: 0;
  transform: translateX(10%);
}

.forward-leave-to {
  opacity: 0;
  transform: translateX(-10%);
}

.back-enter {
  opacity: 0;
  transform: translateX(-10%);
}

.back-leave-to {
  opacity: 0;
  transform: translateX(10%);
}

.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(20px);
  opacity: 0;
}
</style>
